/**
 * Derives a stable visual identity for a place with no network call and no
 * third-party asset.
 *
 * Why generated rather than fetched: only 17 of the 87 places are national
 * chains with an obtainable brand logo. The other 70 are local
 * independents and institutions that no logo service carries. A brand-logo
 * integration therefore covers about a fifth of the app, so the generated
 * mark is the primary system rather than a fallback — see
 * docs/place-imagery-plan.md.
 *
 * Everything here is pure and deterministic: the same slug always yields
 * the same initials and the same tint, so a place does not change
 * appearance between renders, sessions or devices.
 */

/** Words that should never become a place's initial. */
const STOPWORDS = new Set([
  'the',
  'at',
  'of',
  'and',
  'a',
  'an',
  'in',
  'on',
  'for',
  'to',
  'by',
  'de',
  'la',
  'le',
]);

/**
 * Trailing location qualifiers. "Publix Super Market at Crescent B
 * Commons" should read PS, not PC — the qualifier says where it is, not
 * what it is.
 */
const QUALIFIER_PATTERNS = [
  /\s*[—–-]\s*Babcock Ranch$/i,
  /\s*\(Babcock Ranch\)$/i,
  /\s*at Babcock Ranch$/i,
  /\s*[—–-]\s*Punta Gorda$/i,
  /\s*\(?Crescent B Commons\)?$/i,
];

/**
 * Initials for a place name. One or two letters, upper case.
 *
 * The awkward cases in this dataset drove the rules:
 *   "M'Xuma Tacos - Mexican Grill"  -> MT   (apostrophe is not a boundary)
 *   "U-Yee Sushi & Grill"           -> UY   (hyphen inside a name IS)
 *   "7-Eleven (Duncan Rd)"          -> 7E   (digits are valid initials)
 *   "The Lake House Kitchen & Bar"  -> LH   ("The" is skipped)
 *   "Publix Super Market at ..."    -> PS   (trailing qualifier stripped)
 *   "ALDI Punta Gorda"              -> AL   (all-caps single word -> first two)
 */
export function placeInitials(name: string): string {
  const original = (name ?? '').trim();
  let cleaned = original;

  // Strip trailing location qualifiers, but NEVER strip away the whole
  // name. "Crescent B Commons" is itself a place in this dataset, and the
  // qualifier pattern that turns "Publix Super Market at Crescent B
  // Commons" into "Publix Super Market" would otherwise reduce it to an
  // empty string and render a "?" tile. Any rule that empties the input is
  // discarded rather than applied.
  const strip = (value: string, re: RegExp): string => {
    const next = value.replace(re, '').trim();
    return next.length > 0 ? next : value;
  };
  for (const re of QUALIFIER_PATTERNS) cleaned = strip(cleaned, re);
  // Drop any remaining parenthetical, e.g. "(Duncan Rd)", "(98 Palms)" —
  // under the same no-emptying rule, since a name could be all parenthesis.
  cleaned = strip(cleaned, /\s*\([^)]*\)/g);

  // Split on whitespace, ampersands, slashes, commas and hyphens BETWEEN
  // words. Apostrophes stay, so M'Xuma survives as one token.
  const tokens = cleaned
    .split(/[\s&/,]+|(?<=\w)-(?=\w)/)
    .map((t) => t.replace(/[^\p{L}\p{N}']/gu, ''))
    .filter(Boolean);

  const meaningful = tokens.filter((t) => !STOPWORDS.has(t.toLowerCase()));
  const use = meaningful.length > 0 ? meaningful : tokens;

  if (use.length === 0) return '?';
  if (use.length === 1) {
    const w = use[0];
    // A single word gives up its first two characters rather than one
    // lonely letter — "ALDI" reads better as AL than A.
    return (w.length > 1 ? w.slice(0, 2) : w).toUpperCase();
  }
  return (use[0][0] + use[1][0]).toUpperCase();
}

/**
 * The tints a generated mark may use. All four are drawn from the Solar
 * Reserve palette rather than generated arbitrarily — the retired palette's
 * mistake was letting several unrelated saturated hues compete at once.
 *
 * Each is dark enough for white initials to clear WCAG AA (4.5:1); the
 * ratios are asserted in tests rather than assumed.
 */
export const AVATAR_TINTS = [
  { name: 'navy', bg: '#173D4E', fg: '#FCFAF5' },
  { name: 'green', bg: '#2F5741', fg: '#FCFAF5' },
  { name: 'brass', bg: '#6E5322', fg: '#FCFAF5' },
  { name: 'terra', bg: '#6B3B2E', fg: '#FCFAF5' },
] as const;

export type AvatarTint = (typeof AVATAR_TINTS)[number];

/**
 * FNV-1a. A real hash rather than summing char codes, because summing
 * gives near-identical values for anagram-ish slugs and clusters the
 * results onto one or two tints.
 */
function hashString(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Picks a tint from the slug. Keyed on slug rather than name so renaming a
 * place for display does not change its colour, and so the same place is
 * identical on every device.
 */
export function placeTint(slug: string): AvatarTint {
  if (!slug) return AVATAR_TINTS[0];
  return AVATAR_TINTS[hashString(slug) % AVATAR_TINTS.length];
}

/** Categories that have a dedicated glyph. Anything else falls back. */
export const GLYPH_CATEGORIES = [
  'food',
  'fitness',
  'grocery',
  'medical',
  'services',
  'shopping',
  'home_improvement',
  'explore',
  'gas',
  'recreation',
  'attraction',
  'travel',
  'babcock_ranch',
  'babcock_national',
] as const;

export type GlyphCategory = (typeof GLYPH_CATEGORIES)[number];

export function isGlyphCategory(value: string | null | undefined): value is GlyphCategory {
  return !!value && (GLYPH_CATEGORIES as readonly string[]).includes(value);
}
